/**
 * Utility for caching supplier customization settings in sessionStorage
 * Allows supplier-specific colors to be applied throughout the purchase flow
 */

const CACHE_KEY = 'supplier_customization_cache';

export interface CachedSupplierCustomization {
  supplierId?: string;
  supplierSlug?: string;
  supplierName?: string;
  whatsappNumber?: string;
  logoUrl?: string;
  accentColor?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  headerColor?: string;
  iconColor?: string;
  source?: string; // 'digital_menu' or 'marketplace'
  menuUrl?: string; // URL do cardápio para navegação de retorno
  eventLocationLabel?: string | null;
  eventDateLabel?: string | null;
  updatedAt?: number;
}

// Default platform colors (AmeUp)
export const DEFAULT_COLORS = {
  accentColor: '#A3E635',
  buttonColor: '#FB583A',
  buttonTextColor: '#FFFFFF',
  headerColor: '#FB583A',
  iconColor: '#FB583A',
};

/**
 * Save supplier customization to sessionStorage
 */
export const saveSupplierCustomization = (customization: Partial<CachedSupplierCustomization>): void => {
  try {
    const existing = loadSupplierCustomization() || {};
    const updated: CachedSupplierCustomization = {
      ...existing,
      ...customization,
      updatedAt: Date.now(),
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error saving supplier customization to cache:', error);
  }
};

/**
 * Load supplier customization from sessionStorage
 */
export const loadSupplierCustomization = (): CachedSupplierCustomization | null => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as CachedSupplierCustomization;
    
    // Check if cache is still valid (24 hours)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    if (parsed.updatedAt && Date.now() - parsed.updatedAt > maxAge) {
      clearSupplierCustomization();
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Error loading supplier customization from cache:', error);
    return null;
  }
};

/**
 * Clear supplier customization from sessionStorage
 */
export const clearSupplierCustomization = (): void => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error('Error clearing supplier customization cache:', error);
  }
};

/**
 * Normalize a customization object that may use snake_case keys
 * (as stored in `supplier_store_customization` DB rows) into the
 * camelCase shape expected by the rest of the app.
 */
const normalizeCustomization = (
  raw?: (Partial<CachedSupplierCustomization> & Record<string, any>) | null
): Partial<CachedSupplierCustomization> | null => {
  if (!raw) return null;
  const r = raw as Record<string, any>;
  const normalized: Partial<CachedSupplierCustomization> = {
    ...raw,
    accentColor: r.accentColor ?? r.accent_color,
    buttonColor: r.buttonColor ?? r.button_color,
    buttonTextColor: r.buttonTextColor ?? r.text_color ?? r.button_text_color,
    headerColor: r.headerColor ?? r.bottom_header_color ?? r.header_color,
    iconColor: r.iconColor ?? r.icon_color,
  };
  return normalized;
};

/**
 * Get supplier colors with fallback to defaults
 */
export const getSupplierColors = (customization?: Partial<CachedSupplierCustomization> | null): {
  accentColor: string;
  buttonColor: string;
  buttonTextColor: string;
  headerColor: string;
  iconColor: string;
} => {
  const normalized = normalizeCustomization(customization as any);
  const data = normalized || loadSupplierCustomization();

  return {
    accentColor: data?.accentColor || DEFAULT_COLORS.accentColor,
    buttonColor: data?.buttonColor || DEFAULT_COLORS.buttonColor,
    buttonTextColor: data?.buttonTextColor || DEFAULT_COLORS.buttonTextColor,
    headerColor: data?.headerColor || DEFAULT_COLORS.headerColor,
    iconColor: data?.iconColor || data?.accentColor || DEFAULT_COLORS.iconColor,
  };
};

/**
 * Get full supplier theme including branding
 */
export const getSupplierTheme = (customization?: Partial<CachedSupplierCustomization> | null): CachedSupplierCustomization & {
  accentColor: string;
  buttonColor: string;
  buttonTextColor: string;
  headerColor: string;
  iconColor: string;
  isDigitalMenu: boolean;
} => {
  const normalized = normalizeCustomization(customization as any);
  const data = normalized || loadSupplierCustomization();
  const colors = getSupplierColors(data || undefined);

  return {
    ...data,
    ...colors,
    isDigitalMenu: data?.source === 'digital_menu' || !!data?.supplierSlug,
  };
};

